<script setup lang="ts">
import { cn } from '@/lib/utils'
import { useVModel } from '@vueuse/core'
import { ref, computed, onMounted, onUnmounted, provide } from 'vue'
import type { HTMLAttributes } from 'vue'

interface Option {
  value: string | number
  label: string
}

const props = defineProps<{
  modelValue?: string | number
  options?: Option[]
  placeholder?: string
  class?: HTMLAttributes['class']
  disabled?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
}>()

const isOpen = ref(false)
const selectRef = ref<HTMLDivElement | null>(null)
const modelValue = useVModel(props, 'modelValue', emits)

// Provide values to child components
provide('select', {
  isOpen,
  modelValue,
  updateModelValue: (value: string | number) => {
    modelValue.value = value
    isOpen.value = false
  }
})

const selectedOption = computed(() => {
  return props.options?.find(option => option.value === modelValue.value)
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (option: Option) => {
  modelValue.value = option.value
  isOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (selectRef.value && !selectRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div
    ref="selectRef"
    class="relative"
    :class="props.class"
  >
    <slot />
  </div>
</template> 